import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  Clock3,
  Info,
  RefreshCw,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatDate, formatRate } from "./market";
import type { FxPair, FxRatesResponse } from "./types";

export function EcbFxStatus({
  data,
  refreshing,
  refresh,
}: {
  data: FxRatesResponse;
  refreshing: boolean;
  refresh: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-white/40">
      <span className={`flex items-center gap-1.5 ${data.stale ? "text-[#E53935]" : "text-[#1FBF75]"}`}>
        {data.stale ? <Clock3 className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
        {data.stale ? "Daten möglicherweise veraltet" : `Referenzdatum ${formatDate(data.referenceDate)}`}
      </span>
      <button
        onClick={refresh}
        className="flex h-9 items-center gap-2 border border-white/10 bg-[#07080d] px-3 font-medium text-white"
      >
        <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        Aktualisieren
      </button>
    </div>
  );
}

export function EcbFxNotice() {
  return (
    <div className="flex items-start gap-3 border border-white/10 bg-[#07080d] p-4 text-xs text-white/40">
      <Info className="mt-0.5 h-4 w-4 flex-none text-white/30" />
      <p>
        Offizielle Referenzkurse der Europäischen Zentralbank, einmal täglich (ca. 16 Uhr CET,
        nur an Handelstagen) aktualisiert. Keine Echtzeitkurse.
      </p>
    </div>
  );
}

export function FxRateCard({
  pair,
  selected,
  onSelect,
}: {
  pair: FxPair;
  selected: boolean;
  onSelect: () => void;
}) {
  const positive = (pair.changePercent ?? 0) >= 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`border p-5 text-left transition-colors ${selected ? "border-[#e01e37] bg-[#07080d]" : "border-white/10 bg-[#07080d] hover:border-white/20"}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-semibold text-white">EUR/{pair.currency}</p>
          <p className="mt-1 font-mono text-xs text-white/40">{pair.name}</p>
        </div>
        {pair.changePercent !== null ? (
          <span className={`flex items-center gap-1 text-xs font-semibold ${positive ? "text-[#1FBF75]" : "text-[#E53935]"}`}>
            {positive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
            {pair.changePercent.toLocaleString("de-DE", { maximumFractionDigits: 2 })}%
          </span>
        ) : (
          <span className="text-xs text-white/40">–</span>
        )}
      </div>
      <p className="mt-6 font-display text-2xl font-bold tabular-nums text-white">
        {pair.rate !== null ? formatRate(pair.rate) : "Nicht verfügbar"}
      </p>
      <p className="mt-3 text-[11px] text-white/30">Referenzkurs vom {formatDate(pair.referenceDate)}</p>
    </button>
  );
}

export function FxHistoryChart({ pair }: { pair: FxPair }) {
  const series = pair.history.map((point) => ({
    date: formatDate(point.date),
    rate: point.rate,
  }));
  return (
    <section className="border border-white/10 bg-[#07080d] p-5 sm:p-6">
      <div>
        <h2 className="font-display text-xl font-bold text-white">EUR/{pair.currency} Verlauf</h2>
        <p className="mt-1 text-sm text-white/40">Letzte {pair.history.length} EZB-Handelstage</p>
      </div>
      {series.length > 1 ? (
        <div className="mt-6 h-80 min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid stroke="rgba(255,255,255,.08)" vertical={false} />
              <XAxis dataKey="date" stroke="#555" tickLine={false} axisLine={false} minTickGap={40} />
              <YAxis stroke="#555" tickLine={false} axisLine={false} width={68} domain={["auto", "auto"]} />
              <Tooltip
                formatter={(v) => [formatRate(Number(v)), "Referenzkurs"]}
                contentStyle={{ background: "#0a0b10", border: "1px solid rgba(255,255,255,.12)", borderRadius: 0, color: "#fff" }}
              />
              <Line type="monotone" dataKey="rate" stroke="#e01e37" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="mt-8 border border-dashed border-white/10 bg-white/[.025] p-10 text-center text-sm text-white/40">
          Keine historischen Daten verfügbar.
        </div>
      )}
    </section>
  );
}
